<template>
    <g-form @submit.prevent="handleSubmit">
        <g-loader v-if="isLoading" />
        <template v-else>
            <g-button icon="times" size="small" @click.prevent="handleCancel"> Close </g-button>
            <g-base-box :border="false" padding="none" class="g-my-4">
                <g-typography
                    v-if="apiStatsLastUpdatedDisplayDate"
                    tag="p"
                    class="g-mb-3"
                    color="grey-5"
                    size="small"
                    :value="`Last synced: ${apiStatsLastUpdatedDisplayDate}`"
                />
                <g-typography
                    v-if="manualStatsLastUpdatedDisplayDate"
                    tag="p"
                    class="g-mb-3"
                    color="grey-5"
                    size="small"
                    :value="`Last overridden: ${manualStatsLastUpdatedDisplayDate}`"
                />
                <g-typography
                    tag="p"
                    class="g-mb-3"
                    value="Content metrics are auto-synced from your post. If there is missing data, you can override below."
                />
            </g-base-box>
            <g-level v-for="fieldName in manualStatFields" :key="fieldName">
                <g-level-item class="g-flex-grow">
                    <g-form-item :label="getFieldLabel(fieldName)" class="g-w-full">
                        <g-input
                            :value="getRealFieldValue(fieldName)"
                            :disabled="true"
                            @change="(e) => handleFieldChange(fieldName, e)"
                        />
                    </g-form-item>
                </g-level-item>
                <g-level-item class="g-flex-grow">
                    <g-form-item :label="getFieldLabel(fieldName)" class="g-w-full">
                        <g-input
                            :value="getFieldValue(fieldName)"
                            :disabled="isFieldDisabled(fieldName)"
                            placeholder="enter override"
                            @change="(e) => handleFieldChange(fieldName, e)"
                        />
                    </g-form-item>
                </g-level-item>
            </g-level>
            <g-button
                shape="round"
                theme="live"
                block
                type="default"
                html-type="submit"
                class="margin-bottom-normal g-mt-3"
                :disabled="isLoading || isSubmitting"
            >
                Update
            </g-button>
        </template>
    </g-form>
</template>

<script>
    import { ref, toRefs, computed, watch } from 'vue-demi';
    import useContactStore from '../../../../stores/contact';
    import useHttp from '../../../../src/composables/use-http';
    import { useContentManualStats } from '../../../../src/composables/use-content-manual-stats';
    import {
        MANUAL_STAT_FIELD_LABEL_MAP,
        MANUAL_STAT_POST_TYPE_FIELD_MAP
    } from '../../../constants/activation-content-metrics';
    import _ from 'lodash';
    import { message } from '@grininc/grin-ui';
    import moment from 'moment';

    export default {
        name: 'UpdateContentManualStats',
        props: {
            contentId: {
                type: Number,
                required: true
            }
        },

        setup(props, { emit }) {
            const { http } = useHttp();
            const contact = useContactStore().data;
            const contactManualStats = useContentManualStats(contact.id);
            const { contentId } = toRefs(props);
            const isLoading = ref(false);
            const isSubmitting = ref(false);
            const content = ref(null);

            const contentType = computed(() => {
                const isReel = _.get(content.value, 'is_reel');
                const isStory = _.get(content.value, 'is_story');

                return isReel ? 'reel' : isStory ? 'story' : 'post';
            });

            const manualStatFields = computed(() =>
                _.get(
                    MANUAL_STAT_POST_TYPE_FIELD_MAP.find((map) => map.type === contentType.value),
                    'fields'
                )
            );
            const manualStatsUpdatedAt = computed(() => content.value?.manual_stats_updated_at || null);
            const statsLastStoredAt = computed(() => content.value?.stats_last_stored_at || null);

            const manualStatInput = ref({
                impressions: null,
                comments: null,
                likes: null,
                saves: null,
                shares: null,
                reach: null,
                views: null,
                replies: null
            });

            const getContent = async (id) => {
                isLoading.value = true;

                try {
                    const response = await contactManualStats.getContent(id);
                    content.value = response?.body || null;
                } catch (response) {
                    handleErrorResponse(response, 'An error occurred getting content');
                    emit('cancel');
                }

                isLoading.value = false;
            };

            const updateManualStats = async (id, input) => {
                isSubmitting.value = true;

                try {
                    const response = await contactManualStats.updateContentManualStats(id, input);
                    content.value = response?.body || null;
                    emit('success', content.value);
                    message.success('Metrics updated successfully');
                } catch (response) {
                    handleErrorResponse(response, 'An error occurred while updating metrics');
                }

                isSubmitting.value = false;
            };

            const handleErrorResponse = (response, defaultMessage = 'An error occurred') => {
                emit('error', response);

                if (response.status === 422) {
                    message.error(response?.body?.message || defaultMessage);
                } else {
                    message.error(defaultMessage);
                }
            };

            watch(contentId, (value) => {
                getContent(value);
            });
            getContent(contentId.value);

            watch(content, (value) => {
                if (value) {
                    manualStatInput.value = manualStatFields.value.reduce((acc, key) => {
                        const inputValue = _.get(manualStatInput.value, key);
                        const existingValue = _.get(value, `stat.manual_${key}`, 0);

                        if (inputValue > 0) {
                            acc[key] = inputValue;
                        } else if (existingValue > 0) {
                            acc[key] = existingValue;
                        } else {
                            acc[key] = null;
                        }

                        return acc;
                    }, {});
                }
            });

            const getFieldLabel = (name) => _.get(MANUAL_STAT_FIELD_LABEL_MAP, name);

            const getFieldValue = (name) => _.get(manualStatInput.value, name, 0);

            const getRealFieldValue = (name, defaultValue = 0) => {
                const realValue = _.get(content.value, `stat.${name}_real`, defaultValue);

                // If no real and manual values, fall back to computed value, except for impressions
                // impressions could include estimated values, which is not considered a real value
                return name !== 'impressions' && realValue === 0 && getFieldValue(name) === 0
                    ? _.get(content.value, `stat.${name}`, defaultValue)
                    : realValue;
            };

            const formatDisplayDate = (dateString) =>
                dateString ? moment(dateString).format('MMM D YYYY h:mm A') : null;

            const handleFieldChange = (name, value) => {
                manualStatInput.value = {
                    ...manualStatInput.value,
                    [name]: value
                };
            };

            return {
                manualStatFields,
                manualStatInput,
                isLoading,
                isSubmitting,
                manualStatsLastUpdatedDisplayDate: computed(() => formatDisplayDate(manualStatsUpdatedAt.value)),
                apiStatsLastUpdatedDisplayDate: computed(() => formatDisplayDate(statsLastStoredAt.value)),
                getFieldLabel,
                getFieldValue,
                handleFieldChange,
                getRealFieldValue,
                isFieldDisabled: (name) => isLoading.value || isSubmitting.value || getRealFieldValue(name) > 0,
                handleSubmit: async () => await updateManualStats(contentId.value, manualStatInput.value),
                handleCancel: () => emit('cancel')
            };
        }
    };
</script>
